import type { User } from "@blocknote/core";
import { CommentsExtension } from "@blocknote/core/comments";
import type { ThreadStore } from "@blocknote/core/comments";
import type { BlockNoteEditor } from "@blocknote/core";

export const createLocalThreadId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `thread-${crypto.randomUUID()}`;
  }
  return `thread-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export type PendingCommentSelection = {
  text: string;
  from: number;
  to: number;
  threadId: string;
};

export type EditorCommentsConfig = {
  threadStore: ThreadStore;
  resolveUsers: (userIds: string[]) => Promise<User[]>;
  onPendingComment?: (selectedText: string, threadId: string) => void;
  onPendingQuoteChange?: (selectedText: string, threadId: string) => void;
  onSelectedThreadChange?: (threadId: string | undefined) => void;
};

type CommentsExtensionInstance = ReturnType<
  ReturnType<typeof CommentsExtension>
>;

type ProseMirrorViewLike = {
  dom?: { isConnected?: boolean };
  docView?: unknown;
};

export const getCommentsExtension = (
  editor: BlockNoteEditor
): CommentsExtensionInstance | undefined => {
  return editor.getExtension(CommentsExtension);
};

export const canUseEditorView = (editor: BlockNoteEditor) => {
  if (editor.headless) return false;
  const view = editor.prosemirrorView as ProseMirrorViewLike | undefined;
  return Boolean(view?.dom?.isConnected && view.docView);
};

export const whenEditorViewReady = (
  editor: BlockNoteEditor,
  onReady: () => void,
  attempt = 0
) => {
  if (canUseEditorView(editor)) {
    onReady();
    return;
  }
  if (attempt >= 40) return;
  window.setTimeout(() => {
    whenEditorViewReady(editor, onReady, attempt + 1);
  }, 25);
};

/** Unwrap CommentsExtension marks only. Persisted thread styles must stay. */
export const stripCommentMarksFromHtml = (html: string): string => {
  if (!html.includes("bn-thread-mark")) return html;

  if (typeof DOMParser === "undefined") {
    return html.replace(
      /<span[^>]*bn-thread-mark(?![^>]*data-style-type)[^>]*>([\s\S]*?)<\/span>/gi,
      "$1"
    );
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("span.bn-thread-mark").forEach((span) => {
    if (span.getAttribute("data-style-type") === "commentThread") return;
    if (span.classList.contains("bn-comment-anchor")) return;
    const parent = span.parentNode;
    if (!parent) return;
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span);
    }
    parent.removeChild(span);
  });
  return doc.body.innerHTML;
};

export const captureEditorSelection = (
  editor: BlockNoteEditor
): PendingCommentSelection | null => {
  if (!canUseEditorView(editor)) return null;

  const selection = editor.prosemirrorState.selection;
  if (selection.empty || selection.from === selection.to) return null;

  const text = editor.prosemirrorState.doc
    .textBetween(selection.from, selection.to, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;

  return {
    text,
    from: selection.from,
    to: selection.to,
    threadId: createLocalThreadId(),
  };
};
